import * as Sentry from '@sentry/react'
import React, { useEffect, useRef } from 'react'

const FEEDBACK_FORM_OPTIONS = {
  formTitle: 'Help Us Improve',
  messagePlaceholder:
    'Provide feedback about features and functionality. To give feedback on a specific Repair Order, use the chat feature.',
  submitButtonLabel: 'Send',
  useSentryUser: {
    name: 'name',
    email: 'email',
  },
  successMessageText:
    'Thank you for your feedback! If you need immediate assistance, please contact us at warp.help@warrantyprocessing.com.',
}

/**
 * Return the Feedback integration, adding it on first use.
 *
 * The integration is added here, not in `Routes`, because a child's effects run
 * before its parent's. When `Routes` added it, this component asked for it first,
 * got nothing, and never wired up the button (WARP-956).
 *
 * It is still added only once a user is signed in, because `Routes` renders this
 * component only then. That keeps the widget's DOM setup off the public pages
 * (WARP-890).
 */
const getFeedbackIntegration = () => {
  const existing = Sentry.getFeedback()
  if (existing) return existing

  Sentry.addIntegration(
    Sentry.feedbackIntegration({
      autoInject: false,
      colorScheme: 'system',
      showBranding: false,
    }),
  )

  return Sentry.getFeedback()
}

const SentryFeedback = () => {
  // Refs
  const buttonRef = useRef(null)

  useEffect(() => {
    const feedback = getFeedbackIntegration()
    if (!feedback || !buttonRef.current) return undefined

    // `attachTo` returns the function that removes its click listener.
    return feedback.attachTo(buttonRef.current, FEEDBACK_FORM_OPTIONS)
  }, [])

  // Without a Sentry client there is no form to open, so do not show a dead button.
  if (!Sentry.getClient()) return null

  return (
    <button
      ref={buttonRef}
      id="feedback-button"
      type="button"
      className="fixed bottom-4 left-4 z-10 cursor-pointer rounded-full bg-white px-4 py-3 shadow-md hover:bg-gray-100 hover:shadow-lg"
    >
      📣 Feedback
    </button>
  )
}

export default SentryFeedback
